import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import '../../style/scss/sms.scss';

// The shape of a manual sms option of a job
interface JobSmsOption {
    template_id: number;
    name: string;
    message_type: string;
    body: string;
    credits: number;
    phone_number: string | null;
    skip_reason: string | null;
}

interface SendJobSmsModalProps {
    jobId: number;
}

const SendJobSmsModal: React.FC<SendJobSmsModalProps> = ({ jobId }) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeModal } = useModal();
    const [options, setOptions] = useState<JobSmsOption[]>([]);
    const [balance, setBalance] = useState<number>(0);
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch the manual sms options of the job
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await fetchData({ apiUrl: `get_job_sms_options/${jobId}`, handleLogout });
                setOptions(response?.options || []);
                setBalance(response?.balance || 0);
            } finally {
                revealModal();
            }
        };
        fetchOptions();
    }, [jobId]);

    // Get the selected option
    const selectedOption = options.find(option => option.template_id === selectedTemplate);

    // Send the selected message
    const handleSend = async () => {
        if (!selectedOption) return;
        setButtonLoader(true);
        setError(null);

        try {
            // Send the manual sms via the backend
            const response = await saveData({
                apiUrl: `send_job_sms/${jobId}`,
                method: 'post',
                data: { template_id: selectedOption.template_id }
            });

            // Close the modal and show a success alert
            if (response?.status === 200) {
                setFloatingAlert({ type: 'success' });
                closeModal();
            }
        } catch (requestError: any) {
            // Show the error reason of the backend
            const reason = requestError?.response?.data?.error;
            setError(reason ? t(`sms_message.skip_reason.${reason}`, { defaultValue: t('sms.send_modal.send_failed_error') }) : t('sms.send_modal.send_failed_error'));
        } finally {
            setButtonLoader(false);
        }
    };

    return (
        <div className='send-job-sms-modal'>
            <h3>{t('sms.send_modal.title')}</h3>

            {error && (
                <div className='alert form-alert alert-danger' role='alert'>{error}</div>
            )}

            {options.length === 0 ? (
                <p className='helper-text'>{t('sms.send_modal.no_options_message')}</p>
            ) : (
                <div className='sms-option-cards'>
                    {options.map(option => (
                        <div
                            key={option.template_id}
                            className={`sms-option-card ${selectedTemplate === option.template_id ? 'selected' : ''} ${option.skip_reason ? 'disabled' : ''}`}
                            onClick={() => !option.skip_reason && setSelectedTemplate(option.template_id)}
                        >
                            <div className='sms-option-header'>
                                <span className='sms-option-name'>{option.name}</span>
                                <span className='sms-option-credits'>
                                    {t('sms.send_modal.credits_label', { credits: option.credits })}
                                </span>
                            </div>
                            <p className='sms-option-body'>{option.body}</p>
                            {option.skip_reason ? (
                                <p className='error-text'>{t(`sms_message.skip_reason.${option.skip_reason}`)}</p>
                            ) : (
                                <p className='helper-text'>{option.phone_number}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <p className='helper-text'>{t('sms.send_modal.balance_label', { balance })}</p>

            {/* The modal buttons */}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        type='button'
                        label='general.cancel'
                        size='small'
                        onClick={() => closeModal()}
                    />
                    <PrimaryButton
                        type='button'
                        label='sms.send_modal.send_button'
                        size='small'
                        loading={buttonLoader}
                        disabled={{ disabled: !selectedOption, message: t('sms.send_modal.select_message_first') }}
                        onClick={handleSend}
                    />
                </div>
            </div>
        </div>
    );
};

export default SendJobSmsModal;
